import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { toast, dlBlob, formatPhone } from '../../lib/utils';

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};
const firstOfMonthStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
};
const minus = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};
const DOW = ['일','월','화','수','목','금','토'];

// ── 연차 신청 달력 모달 (급여관리 출근표 달력과 동일 스타일) ──
function LeaveCalendarModal({ plan, onClose }) {
  const [y, m] = String(plan.target_month || '').split('-').map(Number);
  const year = y, month = m;
  const pad = n => String(n).padStart(2, '0');
  const dateSet = new Set(plan.dates || []);
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDow = new Date(year, month - 1, 1).getDay();

  return (
    <div style={{position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center'}} onClick={onClose}>
      <div style={{position:'absolute', inset:0, background:'rgba(0,0,0,0.5)'}}/>
      <div style={{position:'relative', background:'#fff', borderRadius:16, padding:'28px', width:'min(880px, 96vw)', maxHeight:'92vh', overflowY:'auto', boxShadow:'0 8px 40px rgba(0,0,0,0.2)'}} onClick={e => e.stopPropagation()}>
        <div style={{display:'flex', alignItems:'center', marginBottom:20}}>
          <div>
            <div style={{fontSize:20, fontWeight:700}}>{plan.manager_name} 연차 신청</div>
            <div style={{fontSize:13, color:'var(--text2)', marginTop:3}}>
              {year}년 {month}월 · {plan.store_name} {plan.branch_name} · 총 <b style={{color:'#1565C0'}}>{(plan.dates||[]).length}</b>일 신청
            </div>
          </div>
          <button onClick={onClose} style={{marginLeft:'auto', background:'none', border:'none', fontSize:24, cursor:'pointer', color:'#999', lineHeight:1}}>✕</button>
        </div>

        <div style={{display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:6, marginBottom:6}}>
          {DOW.map((d,i) => (
            <div key={d} style={{textAlign:'center', fontSize:13, fontWeight:700, padding:'8px 0', background:'#f5f5f5', borderRadius:6,
              color: i===0?'#c62828':i===6?'#1565C0':'var(--text3)'}}>{d}</div>
          ))}
        </div>
        <div style={{display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:6}}>
          {Array.from({length: firstDow}).map((_,i) => <div key={`e${i}`}/>)}
          {Array.from({length: daysInMonth}).map((_,i) => {
            const day = i + 1;
            const dateStr = `${year}-${pad(month)}-${pad(day)}`;
            const dow = (firstDow + i) % 7;
            const isSun = dow === 0, isSat = dow === 6;
            const isLeave = dateSet.has(dateStr);
            return (
              <div key={day} style={{borderRadius:8, padding:'8px', minHeight:74,
                background: isLeave ? '#e3f2fd' : '#fafafa',
                border: `1px solid ${isLeave ? '#64b5f6' : '#e0e0e0'}`}}>
                <div style={{fontSize:14, fontWeight:700, marginBottom:4,
                  color: isSun?'#c62828': isSat?'#1565C0':'var(--text)'}}>{day}</div>
                {isLeave && (
                  <div style={{fontSize:11, fontWeight:700, color:'#1565C0', background:'#fff', border:'1px solid #90caf9', borderRadius:4, padding:'2px 4px', textAlign:'center'}}>📅 연차</div>
                )}
              </div>
            );
          })}
        </div>

        {plan.memo && (
          <div style={{marginTop:16, padding:'10px 14px', background:'#f8f8f8', borderRadius:8, fontSize:12, color:'var(--text2)'}}>
            📝 메모: {plan.memo}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AttendanceMgmtPage({ profile }) {
  const canEdit = !!profile && (profile.role === 'admin' || profile.job_title === '담당자');
  const [editCell, setEditCell] = useState(null); // { row, ds, attId, clock_in, clock_out }
  const [editIn,   setEditIn]   = useState('');
  const [editOut,  setEditOut]  = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (row, ds) => {
    if (!canEdit) return;
    const c = row.cells[ds];
    setEditCell({ row, ds, attId: c.attId });
    // ISO → 'HH:MM' (KST 환경에 맞춰 local)
    const toHm = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
    };
    setEditIn(toHm(c.clock_in));
    setEditOut(toHm(c.clock_out));
  };
  const closeEdit = () => { setEditCell(null); setEditIn(''); setEditOut(''); };

  const hmToIso = (date, hm) => {
    if (!hm || !/^\d{2}:\d{2}$/.test(hm)) return null;
    const [h, m] = hm.split(':').map(Number);
    const d = new Date(date + 'T00:00:00');
    d.setHours(h, m, 0, 0);
    return d.toISOString();
  };

  const saveEdit = async () => {
    if (!editCell) return;
    const { row, ds, attId } = editCell;
    const inIso  = hmToIso(ds, editIn);
    const outIso = hmToIso(ds, editOut);
    setEditSaving(true);
    let error;
    if (attId) {
      // 기존 row 업데이트
      ({ error } = await supabase.from('attendance').update({
        clock_in:  inIso,
        clock_out: outIso,
      }).eq('id', attId));
    } else if (inIso || outIso) {
      // 신규 row 삽입 (해당 매장·매니저·날짜로)
      // store_account_id 가져오기 — storeMembers에서 매칭
      const sm = storeMembers.find(m =>
        m.name === row.name && m.store_name === row.store_name && m.branch_name === row.branch_name);
      const managerId = sm?.store_account_id || null;
      if (!managerId) {
        toast('이 행은 매장 등록 정보가 없어 신규 추가가 불가합니다 (기타근무자 등)', 'err');
        setEditSaving(false);
        return;
      }
      ({ error } = await supabase.from('attendance').insert({
        manager_id:   managerId,
        manager_name: row.name,
        store_name:   row.store_name,
        branch_name:  row.branch_name,
        work_date:    ds,
        clock_in:     inIso,
        clock_out:    outIso,
      }));
    }
    setEditSaving(false);
    if (error) { toast(error.message, 'err'); return; }
    toast('수정 완료', 'ok');
    closeEdit();
    fetchAll();
  };

  const deleteAtt = async () => {
    if (!editCell?.attId) return;
    if (!window.confirm(`${editCell.row.display_name} - ${editCell.ds} 출퇴근 기록을 삭제하시겠습니까?`)) return;
    setEditSaving(true);
    const { error } = await supabase.from('attendance').delete().eq('id', editCell.attId);
    setEditSaving(false);
    if (error) { toast(error.message, 'err'); return; }
    toast('삭제 완료', 'inf');
    closeEdit();
    fetchAll();
  };

  const [tab,         setTab]         = useState('attendance');
  const [records,     setRecords]     = useState([]);
  const [storeMembers,setStoreMembers]= useState([]);
  const [plans,       setPlans]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [fStore,      setFStore]      = useState('');
  const [fManager,    setFManager]    = useState('');
  const [fFrom,       setFFrom]       = useState(minus(4));
  const [fTo,         setFTo]         = useState(todayStr());

  // 근무자별 이력 탭 (history)
  const [hStore,      setHStore]      = useState('');
  const [hMemberKey,  setHMemberKey]  = useState(''); // store_members.id
  const [hFrom,       setHFrom]       = useState(firstOfMonthStr());
  const [hTo,         setHTo]         = useState(todayStr());
  const [hRecords,    setHRecords]    = useState([]);
  const [hLoading,    setHLoading]    = useState(false);

  const [closures, setClosures] = useState([]); // 휴점일 데이터
  const [leaveCalTarget, setLeaveCalTarget] = useState(null); // 연차 달력 모달 대상 plan
  const [rejectPlan, setRejectPlan] = useState(null); // 반려 사유 모달 대상 plan
  const [rejectReason, setRejectReason] = useState('');
  // 근무자 현황 (본사)
  const [workers, setWorkers] = useState([]);
  const [loadingW, setLoadingW] = useState(false);
  const [savingW, setSavingW] = useState(null); // store_members id
  const [wStore, setWStore] = useState('');
  const [showResigned, setShowResigned] = useState(false);
  const [resignTarget, setResignTarget] = useState(null); // 퇴사일 모달 대상
  const [resignDate, setResignDate] = useState('');
  const [storeAccounts, setStoreAccounts] = useState([]); // 매장 계정(점포/지점)
  const [showAddW, setShowAddW] = useState(false);
  const [aStore, setAStore] = useState(''); const [aBranch, setABranch] = useState('');
  const [aName, setAName] = useState(''); const [aPhone, setAPhone] = useState('');
  const [aJob, setAJob] = useState('매니저'); const [aHire, setAHire] = useState('');
  const [aSalaryType, setASalaryType] = useState('월급'); const [aSalary, setASalary] = useState(''); const [aExtra, setAExtra] = useState('');
  const [aAffil, setAAffil] = useState('한국생활건강');
  const JOB_TITLES = ['매니저', '부매니저'];
  const AFFILIATIONS = ['한국생활건강', '신우'];

  const fetchWorkers = useCallback(async () => {
    setLoadingW(true);
    const [{ data: w }, { data: accts }] = await Promise.all([
      supabase.from('store_members')
        .select('id, name, display_name, job_title, affiliation, phone, hire_date, resigned_at, is_primary, store_account_id, store:profiles!store_account_id(department, branch)')
        .order('store_account_id'),
      supabase.from('profiles').select('id, department, branch').eq('approved', true),
    ]);
    setWorkers(w || []);
    setStoreAccounts((accts || []).filter(a => a.department && a.branch));
    setLoadingW(false);
  }, []);

  const submitAddWorker = async () => {
    if (!aStore || !aBranch) { toast('점포/지점을 선택해주세요', 'err'); return; }
    if (!aName.trim()) { toast('이름을 입력해주세요', 'err'); return; }
    const acct = storeAccounts.find(a => a.department === aStore && a.branch === aBranch);
    if (!acct) { toast('선택한 점포/지점의 매장 계정을 찾을 수 없습니다', 'err'); return; }
    const salaryNum = Number(String(aSalary).replace(/,/g, '')) || 0;
    if (salaryNum <= 0) { toast('급여 금액을 입력해주세요', 'err'); return; }
    setSavingW('new');
    const { data, error } = await supabase.from('store_members').insert({
      store_account_id: acct.id,
      name: aName.trim(), display_name: aName.trim(),
      phone: aPhone.trim() || null, job_title: aJob, hire_date: aHire || null,
      affiliation: aAffil,
      salary_type: aSalaryType,
      salary: salaryNum,
      extra_pay: aSalaryType === '일급' ? (Number(String(aExtra).replace(/,/g, '')) || 0) : 0,
    }).select('id').single();
    setSavingW(null);
    if (error) { toast(error.message, 'err'); return; }
    toast(`${aName.trim()} 근무자 추가 완료`, 'ok');
    setShowAddW(false);
    setAStore(''); setABranch(''); setAName(''); setAPhone(''); setAJob('매니저'); setAHire(''); setAAffil('한국생활건강');
    setASalaryType('월급'); setASalary(''); setAExtra('');
    fetchWorkers();
  };

  // 근무자 정보 수정
  const [eWorker, setEWorker] = useState(null);
  const [eName, setEName] = useState(''); const [eStore, setEStore] = useState(''); const [eBranch, setEBranch] = useState('');
  const [ePhone, setEPhone] = useState(''); const [eJob, setEJob] = useState('매니저');
  const [eAffil, setEAffil] = useState('');
  const openEditWorker = (m) => {
    setEWorker(m);
    setEName(m.display_name || m.name || '');
    setEStore(m.store?.department || ''); setEBranch(m.store?.branch || '');
    setEPhone(m.phone || ''); setEJob(m.job_title || '매니저'); setEAffil(m.affiliation || '');
  };
  const submitEditWorker = async () => {
    if (!eWorker) return;
    if (!eName.trim()) { toast('이름을 입력해주세요', 'err'); return; }
    if (!eStore || !eBranch) { toast('점포/지점을 선택해주세요', 'err'); return; }
    const acct = storeAccounts.find(a => a.department === eStore && a.branch === eBranch);
    if (!acct) { toast('선택한 점포/지점의 매장 계정을 찾을 수 없습니다', 'err'); return; }
    setSavingW(eWorker.id);
    const { error } = await supabase.from('store_members').update({
      name: eName.trim(), display_name: eName.trim(),
      phone: ePhone.trim() || null, job_title: eJob, store_account_id: acct.id,
      affiliation: eAffil || null,
    }).eq('id', eWorker.id);
    if (error) { setSavingW(null); toast(error.message, 'err'); return; }
    // 대표 매니저(is_primary) 정보수정 시 배송정보(발주/SCM 엑셀용 store_addresses) 수취인 전화번호도 동기화
    let syncedAddr = false;
    if (eWorker.is_primary && ePhone.trim()) {
      const { error: aErr, count } = await supabase.from('store_addresses')
        .update({ recipient_phone: ePhone.trim(), updated_at: new Date().toISOString() }, { count: 'exact' })
        .eq('store_name', eStore).eq('branch_name', eBranch);
      if (!aErr && count) syncedAddr = true;
    }
    setSavingW(null);
    toast(syncedAddr ? '정보 수정 완료 · 발주 배송 전화번호도 반영됨' : '정보 수정 완료', 'ok');
    setEWorker(null);
    fetchWorkers();
  };

  const toggleResign = async (m) => {
    if (m.resigned_at) {
      // 복귀
      if (!window.confirm(`${m.display_name || m.name} 님을 복귀(재직) 처리하시겠습니까?`)) return;
      setSavingW(m.id);
      const { error } = await supabase.from('store_members').update({ resigned_at: null }).eq('id', m.id);
      setSavingW(null);
      if (error) { toast(error.message, 'err'); return; }
      toast('복귀 처리 완료', 'ok');
      setWorkers(prev => prev.map(x => x.id === m.id ? { ...x, resigned_at: null } : x));
    } else {
      // 퇴사 → 날짜 선택 모달
      setResignTarget(m); setResignDate(todayStr());
    }
  };
  const submitResign = async () => {
    if (!resignTarget) return;
    if (!resignDate) { toast('퇴사일을 선택해주세요', 'err'); return; }
    setSavingW(resignTarget.id);
    const { error } = await supabase.from('store_members').update({ resigned_at: resignDate }).eq('id', resignTarget.id);
    setSavingW(null);
    if (error) { toast(error.message, 'err'); return; }
    toast(`${resignTarget.display_name || resignTarget.name} 퇴사 처리 (${resignDate})`, 'ok');
    setWorkers(prev => prev.map(x => x.id === resignTarget.id ? { ...x, resigned_at: resignDate } : x));
    setResignTarget(null);
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [{ data: att }, { data: lp }, { data: sm }, { data: cl }] = await Promise.all([
      supabase.from('attendance').select('*')
        .gte('work_date', fFrom).lte('work_date', fTo)
        .order('work_date', { ascending: true }),
      supabase.from('leave_plans').select('*')
        .order('created_at', { ascending: false }),
      supabase.from('store_members')
        .select('id, name, display_name, job_title, store_account_id, store:profiles!store_account_id(department, branch)')
        .is('resigned_at', null)
        .order('id', { ascending: true }),
      supabase.from('store_closures').select('store_name, branch_name, dates'),
    ]);
    setClosures(cl || []);
    setRecords(att || []);
    setPlans(lp || []);
    setStoreMembers((sm || []).map(m => ({
      sm_id: m.id,
      name: m.name,
      display_name: m.display_name || m.name,
      job_title: m.job_title,
      store_account_id: m.store_account_id,
      store_name: m.store?.department || '',
      branch_name: m.store?.branch || '',
    })));
    setLoading(false);
  }, [fFrom, fTo]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fmt = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  };
  const duration = (ci, co) => {
    if (!ci || !co) return '-';
    const m = Math.round((new Date(co) - new Date(ci)) / 60000);
    return `${Math.floor(m/60)}h ${m%60}m`;
  };

  // 매장 = 등록된 모든 매장 (출퇴근 기록 없어도 표시)
  const stores = useMemo(() =>
    [...new Set(storeMembers.map(m => m.store_name).filter(Boolean))].sort(),
    [storeMembers]
  );
  const managers = useMemo(() => {
    const ms = storeMembers
      .filter(m => !fStore || m.store_name === fStore)
      .map(m => m.display_name)
      .filter(Boolean);
    return [...new Set(ms)].sort();
  }, [storeMembers, fStore]);

  // 조회 기간의 날짜 리스트 (헤더 컬럼용)
  const dateList = useMemo(() => {
    const arr = [];
    if (!fFrom || !fTo) return arr;
    const start = new Date(fFrom);
    const end   = new Date(fTo);
    if (isNaN(start) || isNaN(end) || start > end) return arr;
    for (let d = new Date(start); d <= end; d.setDate(d.getDate()+1)) {
      arr.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
    }
    return arr;
  }, [fFrom, fTo]);

  // 매장 매니저별 × 일자별 셀 구성 (피벗).
  // 각 셀: { clock_in, clock_out, isClosed, isOnLeave }
  const displayRows = useMemo(() => {
    // attendance lookup
    const attLookup = new Map(); // 'store|branch|name|date' → rec
    for (const r of records) {
      attLookup.set(`${r.store_name}|${r.branch_name}|${r.manager_name}|${r.work_date}`, r);
    }
    // 매장별 휴점일 set
    const closureMap = new Map();
    for (const c of closures) {
      const key = `${c.store_name}|${c.branch_name}`;
      if (!closureMap.has(key)) closureMap.set(key, new Set());
      for (const d of (c.dates || [])) closureMap.get(key).add(d);
    }
    // 매니저별 휴무신청 set (manager_id + manager_name 기준)
    const leaveMap = new Map();
    for (const p of plans) {
      const key = `${p.manager_id}|${p.manager_name}`;
      if (!leaveMap.has(key)) leaveMap.set(key, new Set());
      for (const d of (p.dates || [])) leaveMap.get(key).add(d);
    }

    const buildCells = (storeName, branchName, accountId, name) => {
      const out = {};
      for (const ds of dateList) {
        const rec = attLookup.get(`${storeName}|${branchName}|${name}|${ds}`);
        out[ds] = {
          clock_in:  rec?.clock_in  || null,
          clock_out: rec?.clock_out || null,
          attId:     rec?.id        || null,
          isClosed:  closureMap.get(`${storeName}|${branchName}`)?.has(ds) || false,
          isOnLeave: leaveMap.get(`${accountId}|${name}`)?.has(ds) || false,
        };
      }
      return out;
    };

    const usedRecordIds = new Set();
    const rows = storeMembers
      .filter(m => m.store_name && m.branch_name)
      .map(m => {
        const cells = buildCells(m.store_name, m.branch_name, m.store_account_id, m.name);
        for (const ds of dateList) {
          if (cells[ds].attId) usedRecordIds.add(cells[ds].attId);
        }
        return {
          key: `sm-${m.sm_id}`,
          name: m.name,
          display_name: m.display_name,
          job_title: m.job_title,
          store_name: m.store_name,
          branch_name: m.branch_name,
          cells,
          isExtra: false,
        };
      });

    // 기타근무자 (storeMembers에 없는 attendance) — name 기준 그룹화
    const extraGroups = new Map();
    for (const r of records) {
      if (usedRecordIds.has(r.id)) continue;
      const gk = `${r.store_name}|${r.branch_name}|${r.manager_name}`;
      if (!extraGroups.has(gk)) {
        const cells = {};
        for (const ds of dateList) {
          cells[ds] = { clock_in:null, clock_out:null, attId:null, isClosed:false, isOnLeave:false };
        }
        extraGroups.set(gk, {
          key: `extra-${gk}`,
          name: r.manager_name,
          display_name: r.manager_name,
          job_title: '기타근무자',
          store_name: r.store_name || '',
          branch_name: r.branch_name || '',
          cells,
          isExtra: true,
        });
      }
      const g = extraGroups.get(gk);
      if (g.cells[r.work_date]) {
        g.cells[r.work_date].clock_in = r.clock_in;
        g.cells[r.work_date].clock_out = r.clock_out;
        g.cells[r.work_date].attId = r.id;
      }
    }
    // 점포·지점·직책 순 정렬 (같은 매장끼리 묶이도록)
    const all = [...rows, ...extraGroups.values()];
    const titleRank = (t) => t === '매니저' ? 0 : t === '부매니저' ? 1 : t === '기타근무자' ? 3 : 2;
    all.sort((a, b) => {
      if (a.store_name !== b.store_name) return (a.store_name || '').localeCompare(b.store_name || '');
      if (a.branch_name !== b.branch_name) return (a.branch_name || '').localeCompare(b.branch_name || '');
      return titleRank(a.job_title) - titleRank(b.job_title);
    });
    return all;
  }, [records, storeMembers, plans, closures, dateList]);

  const filteredAtt = useMemo(() => {
    let r = displayRows;
    if (fStore)   r = r.filter(x => x.store_name === fStore);
    if (fManager) r = r.filter(x => x.display_name === fManager);
    // 그룹화 정보: 같은 (store, branch) 행끼리 묶어 rowSpan 처리
    const groupSizes = new Map();
    for (const x of r) {
      const k = `${x.store_name}|${x.branch_name}`;
      groupSizes.set(k, (groupSizes.get(k) || 0) + 1);
    }
    const seen = new Set();
    return r.map(x => {
      const k = `${x.store_name}|${x.branch_name}`;
      const isFirstOfGroup = !seen.has(k);
      seen.add(k);
      return { ...x, isFirstOfGroup, groupSize: groupSizes.get(k) };
    });
  }, [displayRows, fStore, fManager]);

  const stat = useMemo(() => {
    const memberCount = filteredAtt.length;
    return { memberCount, days: dateList.length };
  }, [filteredAtt, dateList]);

  const newPlanCount = plans.filter(p => p.status === 'pending').length;

  // 근무자별 이력 — 점포·근무자·기간 변경 시 자동 조회
  const hMember = useMemo(
    () => storeMembers.find(m => String(m.sm_id) === String(hMemberKey)),
    [storeMembers, hMemberKey]
  );
  const fetchHistory = useCallback(async () => {
    if (!hMember || !hFrom || !hTo) { setHRecords([]); return; }
    setHLoading(true);
    const { data } = await supabase.from('attendance').select('*')
      .eq('manager_id', hMember.store_account_id)
      .eq('manager_name', hMember.name)
      .gte('work_date', hFrom).lte('work_date', hTo)
      .order('work_date', { ascending: false });
    setHRecords(data || []);
    setHLoading(false);
  }, [hMember, hFrom, hTo]);
  useEffect(() => {
    if (tab !== 'history') return;
    fetchHistory();
  }, [tab, fetchHistory]);

  useEffect(() => { if (tab === 'workers') fetchWorkers(); }, [tab, fetchWorkers]);

  // 근무자현황 엑셀 다운로드 (정보수정·퇴사처리 열 제외)
  const exportWorkers = async (list) => {
    if (!list || list.length === 0) { toast('내보낼 근무자가 없습니다', 'err'); return; }
    const ExcelJS = (await import('exceljs')).default;
    const { dlBlob } = await import('../../lib/utils');
    const FONT = { name: '맑은 고딕', size: 10 };
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('근무자현황');
    ws.columns = [
      { header: '점포', width: 14 }, { header: '지점', width: 14 }, { header: '이름', width: 12 },
      { header: '직책', width: 10 }, { header: '소속', width: 12 }, { header: '연락처', width: 16 },
      { header: '입사일', width: 12 }, { header: '상태', width: 16 },
    ];
    const hr = ws.getRow(1); hr.font = { ...FONT, bold: true }; hr.alignment = { horizontal: 'center', vertical: 'middle' };
    hr.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEEEEE' } }; c.border = { bottom: { style: 'thin' } }; });
    for (const m of list) {
      const status = m.resigned_at ? `퇴사 ${String(m.resigned_at).slice(0, 10)}` : '재직';
      const row = ws.addRow([
        m.store?.department || '', m.store?.branch || '', m.display_name || m.name || '',
        m.job_title || '', m.affiliation || '', m.phone || '', m.hire_date || '', status,
      ]);
      row.font = m.resigned_at ? { ...FONT, color: { argb: 'FF999999' } } : FONT;
    }
    ws.views = [{ state: 'frozen', ySplit: 1 }];
    const buf = await wb.xlsx.writeBuffer();
    dlBlob(buf, `근무자현황_${todayStr()}.xlsx`);
  };

  // 기간 내 모든 날짜 × attendance 매핑 (미체크 표시 포함)
  const hDisplayRows = useMemo(() => {
    if (!hMember || !hFrom || !hTo) return [];
    const lookup = new Map();
    for (const r of hRecords) lookup.set(r.work_date, r);
    // 해당 매장 휴점일 set
    const closureSet = new Set();
    for (const c of closures) {
      if (c.store_name === hMember.store_name && c.branch_name === hMember.branch_name) {
        (c.dates || []).forEach(d => closureSet.add(d));
      }
    }
    const rows = [];
    const start = new Date(hFrom);
    const end   = new Date(hTo);
    if (isNaN(start) || isNaN(end) || start > end) return [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate()+1)) {
      const ds = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      const rec = lookup.get(ds);
      rows.push({
        date: ds, dow: DOW[d.getDay()],
        clock_in: rec?.clock_in || null,
        clock_out: rec?.clock_out || null,
        isClosed: closureSet.has(ds),
      });
    }
    return rows.reverse(); // 최신 날짜가 위로
  }, [hMember, hFrom, hTo, hRecords, closures]);

  const hStat = useMemo(() => {
    const total    = hDisplayRows.length;
    const worked   = hDisplayRows.filter(r => r.clock_in).length;
    const closedDays = hDisplayRows.filter(r => r.isClosed && !r.clock_in).length;
    const missing  = total - worked - closedDays;
    const totalMin = hDisplayRows.reduce((s,r) => {
      if (!r.clock_in || !r.clock_out) return s;
      return s + Math.round((new Date(r.clock_out) - new Date(r.clock_in)) / 60000);
    }, 0);
    const hours = `${Math.floor(totalMin/60)}h ${totalMin%60}m`;
    return { total, worked, missing, closedDays, hours };
  }, [hDisplayRows]);

  const handleDownloadAttendance = async () => {
    try {
      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('출퇴근현황');

      // 헤더: 점포 / 지점 / 근무자 / 직책 / [일자별 컬럼...]
      const header = ['점포', '지점', '근무자', '직책', ...dateList.map(ds => {
        const dow = new Date(ds).getDay();
        return `${ds.slice(5)} (${DOW[dow]})`;
      })];
      const hr = ws.addRow(header);
      hr.eachCell(cell => {
        cell.font = { bold: true };
        cell.fill = { type:'pattern', pattern:'solid', fgColor:{argb:'FFEEEEEE'} };
        cell.alignment = { horizontal:'center', vertical:'middle', wrapText:true };
        cell.border = { top:{style:'thin'}, bottom:{style:'thin'}, left:{style:'thin'}, right:{style:'thin'} };
      });
      hr.height = 28;

      // 데이터 행 (근무자 단위)
      for (const r of filteredAtt) {
        const row = ws.addRow([
          r.store_name || '-',
          r.branch_name || '-',
          r.display_name || r.name || '-',
          r.job_title || (r.isExtra ? '기타근무자' : '-'),
          ...dateList.map(ds => {
            const c = r.cells[ds];
            if (c.clock_in || c.clock_out) {
              const inTxt  = c.clock_in  ? fmt(c.clock_in)  : '-';
              const outTxt = c.clock_out ? fmt(c.clock_out) : '-';
              return `${inTxt}\n${outTxt}`;
            }
            if (c.isClosed)  return '휴점';
            if (c.isOnLeave) return '휴무';
            return '미체크';
          }),
        ]);
        row.eachCell({ includeEmpty:true }, (cell, colNum) => {
          cell.alignment = { horizontal:'center', vertical:'middle', wrapText:true };
          cell.border = { top:{style:'thin'}, bottom:{style:'thin'}, left:{style:'thin'}, right:{style:'thin'} };
          if (colNum > 4) {
            const v = String(cell.value || '');
            if (v === '휴점')   cell.font = { color:{argb:'FF6A1B9A'}, bold:true };
            else if (v === '휴무') cell.font = { color:{argb:'FF1565C0'}, bold:true };
            else if (v === '미체크') cell.font = { color:{argb:'FFC62828'}, bold:true };
          }
        });
        row.height = 32; // 출근/퇴근 두 줄 표시 위함
      }

      // 컬럼 폭
      ws.getColumn(1).width = 14;  // 점포
      ws.getColumn(2).width = 14;  // 지점
      ws.getColumn(3).width = 10;  // 근무자
      ws.getColumn(4).width = 8;   // 직책
      for (let i = 5; i <= 4 + dateList.length; i++) {
        ws.getColumn(i).width = 12;
      }

      // 같은 점포·지점 행 병합 (화면 rowSpan과 동일 효과)
      // filteredAtt[i] → 엑셀 row (i+2)  [헤더가 row 1]
      let groupStart = 2;
      for (let i = 1; i < filteredAtt.length; i++) {
        const cur = filteredAtt[i];
        const prev = filteredAtt[i - 1];
        if (cur.store_name !== prev.store_name || cur.branch_name !== prev.branch_name) {
          const groupEnd = i + 1; // 직전 그룹 마지막 엑셀 row
          if (groupEnd > groupStart) {
            ws.mergeCells(groupStart, 1, groupEnd, 1);
            ws.mergeCells(groupStart, 2, groupEnd, 2);
          }
          groupStart = i + 2; // 새 그룹 시작 엑셀 row
        }
      }
      // 마지막 그룹 마무리
      if (filteredAtt.length > 0) {
        const lastEnd = filteredAtt.length + 1;
        if (lastEnd > groupStart) {
          ws.mergeCells(groupStart, 1, lastEnd, 1);
          ws.mergeCells(groupStart, 2, lastEnd, 2);
        }
      }

      const buf = await wb.xlsx.writeBuffer();
      dlBlob(buf, `출퇴근현황_${fFrom}_${fTo}.xlsx`);
      toast(`엑셀 다운로드 완료 (${filteredAtt.length}명 × ${dateList.length}일)`, 'ok');
    } catch (err) {
      toast('다운로드 실패: ' + (err.message || err), 'err');
    }
  };

  const updatePlanStatus = async (id, status, reason = null) => {
    const patch = { status };
    if (status === 'rejected') patch.reject_reason = (reason || '').trim() || null;
    if (status === 'approved') patch.reject_reason = null; // 승인 시 기존 사유 제거
    const { error } = await supabase.from('leave_plans').update(patch).eq('id', id);
    if (error) toast(error.message, 'err');
    else { toast(status === 'approved' ? '확인 완료' : '반려 처리', 'ok'); fetchAll(); }
  };
  const submitReject = async () => {
    if (!rejectPlan) return;
    if (!rejectReason.trim()) { toast('반려 사유를 입력해주세요', 'err'); return; }
    await updatePlanStatus(rejectPlan.id, 'rejected', rejectReason);
    setRejectPlan(null); setRejectReason('');
  };

  return (
    <div>
      {/* 출퇴근 시각 편집 모달 (본사 담당자·어드민만) */}
      {editCell && (
        <div style={{position:'fixed', inset:0, zIndex:300, display:'flex', alignItems:'center', justifyContent:'center'}}>
          <div style={{position:'absolute', inset:0, background:'rgba(0,0,0,0.5)'}} onClick={() => !editSaving && closeEdit()}/>
          <div style={{position:'relative', background:'#fff', borderRadius:12, padding:'22px 24px', width:380, maxWidth:'92vw', boxShadow:'0 8px 40px rgba(0,0,0,0.22)'}}>
            <div style={{fontSize:16, fontWeight:700, marginBottom:6}}>출퇴근 시각 수정</div>
            <div style={{fontSize:12, color:'var(--text3)', marginBottom:16}}>
              {editCell.row.store_name} · {editCell.row.branch_name} · <strong>{editCell.row.display_name}</strong> · {editCell.ds}
            </div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:18}}>
              <div>
                <label style={{display:'block', fontSize:11, color:'var(--text2)', fontWeight:600, marginBottom:4}}>출근 시각</label>
                <input type="time" value={editIn} onChange={e => setEditIn(e.target.value)}
                  style={{width:'100%', height:38, padding:'0 10px', border:'1px solid var(--border)', borderRadius:'var(--radius)', fontSize:14, outline:'none'}}/>
              </div>
              <div>
                <label style={{display:'block', fontSize:11, color:'var(--text2)', fontWeight:600, marginBottom:4}}>퇴근 시각</label>
                <input type="time" value={editOut} onChange={e => setEditOut(e.target.value)}
                  style={{width:'100%', height:38, padding:'0 10px', border:'1px solid var(--border)', borderRadius:'var(--radius)', fontSize:14, outline:'none'}}/>
              </div>
            </div>
            <div style={{fontSize:11, color:'var(--text3)', marginBottom:14, lineHeight:1.5}}>
              💡 빈 칸으로 두면 시각이 비워집니다. 둘 다 비우려면 [삭제] 사용 권장.
            </div>
            <div style={{display:'flex', gap:8}}>
              {editCell.attId && (
                <button type="button" onClick={deleteAtt} disabled={editSaving}
                  style={{flex:1, height:40, border:'1px solid var(--danger)', background:'#fff', color:'var(--danger)', borderRadius:'var(--radius)', fontSize:13, fontWeight:700, cursor:'pointer'}}>
                  🗑️ 삭제
                </button>
              )}
              <button type="button" onClick={closeEdit} disabled={editSaving}
                style={{flex:1, height:40, border:'1px solid var(--border)', background:'#fff', color:'var(--text2)', borderRadius:'var(--radius)', fontSize:13, fontWeight:600, cursor:'pointer'}}>
                취소
              </button>
              <button type="button" onClick={saveEdit} disabled={editSaving}
                style={{flex:2, height:40, border:'none', background:'var(--accent)', color:'#fff', borderRadius:'var(--radius)', fontSize:14, fontWeight:700, cursor:'pointer'}}>
                {editSaving ? <span className="spinner"/> : '✅ 저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="tabs">
        <button className={`tab ${tab==='attendance'?'on':''}`} onClick={() => setTab('attendance')}>출퇴근 현황</button>
        <button className={`tab ${tab==='history'?'on':''}`} onClick={() => setTab('history')}>근무자별 이력</button>
        <button className={`tab ${tab==='workers'?'on':''}`} onClick={() => setTab('workers')}>근무자현황</button>
        <button className={`tab ${tab==='leave'?'on':''}`} onClick={() => setTab('leave')}>
          연차계획
          {newPlanCount > 0 && (
            <span style={{marginLeft:6, background:'var(--danger)', color:'#fff', borderRadius:10, padding:'1px 7px', fontSize:10, fontWeight:700}}>
              NEW {newPlanCount}
            </span>
          )}
        </button>
      </div>

      {tab === 'attendance' && (
        <div className="card" style={{padding:'16px 20px'}}>
          <div className="fbar" style={{flexWrap:'wrap'}}>
            <input type="date" className="fsel" value={fFrom}
              onChange={e => setFFrom(e.target.value || todayStr())}/>
            <span style={{fontSize:12, color:'var(--text3)'}}>~</span>
            <input type="date" className="fsel" value={fTo}
              onChange={e => setFTo(e.target.value || todayStr())}/>
            <button className="btn btn-s" onClick={() => { setFFrom(todayStr()); setFTo(todayStr()); }}>오늘</button>
            <button className="btn btn-s" onClick={() => { setFFrom(minus(4)); setFTo(todayStr()); }}>최근 5일</button>
            <select className="fsel" value={fStore} onChange={e => { setFStore(e.target.value); setFManager(''); }}>
              <option value="">전체 점포</option>{stores.map(s => <option key={s}>{s}</option>)}
            </select>
            <select className="fsel" value={fManager} onChange={e => setFManager(e.target.value)}>
              <option value="">전체 근무자</option>{managers.map(m => <option key={m}>{m}</option>)}
            </select>
            <button type="button" onClick={handleDownloadAttendance}
              title={`${fFrom} ~ ${fTo} 출퇴근현황을 엑셀로 다운로드`}
              style={{height:30, padding:'0 12px', border:'1px solid var(--accent)', borderRadius:'var(--radius)', background:'#fff3e0', color:'var(--accent)', fontSize:12, fontWeight:700, cursor:'pointer'}}>
              📥 엑셀
            </button>
            {(fStore||fManager) && (
              <button className="btn-ghost" onClick={() => { setFStore(''); setFManager(''); }}>✕ 초기화</button>
            )}
            <div className="fbar-right" style={{display:'flex', alignItems:'center', gap:14}}>
              <span className="fresult">근무자 <b>{stat.memberCount}</b>명 · 기간 <b>{stat.days}</b>일</span>
            </div>
          </div>

          {loading ? <div className="empty"><span className="spinner"/></div> : (
            <div className="twrap">
              <table>
                <thead>
                  <tr>
                    <th>점포</th>
                    <th>지점</th>
                    <th>근무자</th>
                    {dateList.map(ds => {
                      const dow = new Date(ds).getDay();
                      return (
                        <th key={ds} style={{textAlign:'center', minWidth:90}}>
                          <div style={{fontSize:11, fontWeight:700}}>{ds.slice(5)}</div>
                          <div style={{fontSize:10, fontWeight:600, color: dow===0?'var(--danger)':dow===6?'var(--accent2)':'var(--text3)'}}>{DOW[dow]}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filteredAtt.length === 0
                    ? <tr><td colSpan={3 + dateList.length} className="empty">등록된 근무자가 없습니다</td></tr>
                    : filteredAtt.map(r => (
                      <tr key={r.key} style={r.isFirstOfGroup ? {borderTop:'2px solid var(--border2)'} : {}}>
                        {r.isFirstOfGroup && (
                          <td rowSpan={r.groupSize} style={{verticalAlign:'middle', background:'#fafafa'}}>
                            <span className="badge badge-dept">{r.store_name || '-'}</span>
                          </td>
                        )}
                        {r.isFirstOfGroup && (
                          <td rowSpan={r.groupSize} style={{verticalAlign:'middle', background:'#fafafa'}}>
                            <span className="badge badge-store">{r.branch_name || '-'}</span>
                          </td>
                        )}
                        <td>
                          <strong>{r.display_name}</strong>
                          {r.job_title && r.job_title !== '매니저' && (
                            <span style={{fontSize:10, color:'var(--text3)', marginLeft:6}}>{r.job_title}</span>
                          )}
                        </td>
                        {dateList.map(ds => {
                          const c = r.cells[ds];
                          const cellClickable = canEdit && !r.isExtra;
                          const onClickCell = cellClickable ? () => openEdit(r, ds) : undefined;
                          const cellStyle = cellClickable ? { cursor:'pointer' } : {};
                          if (c.clock_in || c.clock_out) {
                            return (
                              <td key={ds} onClick={onClickCell}
                                title={cellClickable ? '클릭해 수정' : ''}
                                style={{textAlign:'center', fontFamily:'var(--mono)', fontSize:11, lineHeight:1.5, ...cellStyle}}>
                                <div style={{color:'var(--success)', fontWeight:700}}>{c.clock_in ? fmt(c.clock_in) : '-'}</div>
                                <div style={{color:'var(--accent)', fontWeight:600}}>{c.clock_out ? fmt(c.clock_out) : '-'}</div>
                              </td>
                            );
                          }
                          const label = c.isClosed ? '🏪 휴점' : c.isOnLeave ? '📅 휴무' : '미체크';
                          const colors = c.isClosed
                            ? { bg:'#f3e5f5', fg:'#6a1b9a', bd:'#ce93d8' }
                            : c.isOnLeave
                            ? { bg:'#e3f2fd', fg:'#1565C0', bd:'#90caf9' }
                            : { bg:'#ffebee', fg:'var(--danger)', bd:'#f48fb1' };
                          return (
                            <td key={ds} onClick={onClickCell}
                              title={cellClickable ? '클릭해 수정' : ''}
                              style={{textAlign:'center', background: c.isClosed?'#faf5fc':c.isOnLeave?'#f7fbfe':'#fafafa', ...cellStyle}}>
                              <span style={{padding:'2px 8px', background:colors.bg, color:colors.fg, border:`1px solid ${colors.bd}`, borderRadius:3, fontSize:11, fontWeight:700, whiteSpace:'nowrap'}}>
                                {label}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'history' && (
        <div className="card" style={{padding:'16px 20px'}}>
          <div className="fbar" style={{flexWrap:'wrap'}}>
            <select className="fsel" value={hStore}
              onChange={e => { setHStore(e.target.value); setHMemberKey(''); }}>
              <option value="">점포 선택</option>
              {stores.map(s => <option key={s}>{s}</option>)}
            </select>
            <select className="fsel" value={hMemberKey}
              onChange={e => setHMemberKey(e.target.value)} disabled={!hStore}
              style={{background:!hStore?'#f0f0f0':'#fff'}}>
              <option value="">근무자 선택</option>
              {storeMembers
                .filter(m => m.store_name === hStore)
                .map(m => (
                  <option key={m.sm_id} value={m.sm_id}>
                    {m.branch_name ? `[${m.branch_name}] ` : ''}{m.display_name}{m.job_title === '부매니저' ? ' (부)' : ''}
                  </option>
                ))
              }
            </select>
            <input type="date" className="fsel" value={hFrom} onChange={e => setHFrom(e.target.value)}/>
            <span style={{fontSize:12, color:'var(--text3)'}}>~</span>
            <input type="date" className="fsel" value={hTo} onChange={e => setHTo(e.target.value)}/>
            <button className="btn btn-s" onClick={() => { setHFrom(firstOfMonthStr()); setHTo(todayStr()); }}>이번달</button>
            <div className="fbar-right" style={{display:'flex', alignItems:'center', gap:14}}>
              {hMember && (
                <>
                  <span className="fresult">대상기간 <b>{hStat.total}</b>일</span>
                  <span style={{fontSize:12, color:'var(--success)', fontWeight:700}}>근무 {hStat.worked}</span>
                  {hStat.closedDays > 0 && <span style={{fontSize:12, color:'#6a1b9a', fontWeight:700}}>휴점 {hStat.closedDays}</span>}
                  <span style={{fontSize:12, color:'var(--danger)', fontWeight:700}}>미체크 {hStat.missing}</span>
                  <span style={{fontSize:12, color:'var(--text2)', fontFamily:'var(--mono)', fontWeight:700}}>총 {hStat.hours}</span>
                </>
              )}
            </div>
          </div>

          {!hMember
            ? <div className="empty" style={{padding:'40px 0', color:'var(--text3)'}}>점포와 근무자를 선택하면 이력이 표시됩니다</div>
            : hLoading
              ? <div className="empty"><span className="spinner"/></div>
              : (
                <div className="twrap">
                  <table>
                    <thead><tr><th>날짜</th><th>요일</th><th>출근</th><th>퇴근</th><th>근무시간</th></tr></thead>
                    <tbody>
                      {hDisplayRows.length === 0
                        ? <tr><td colSpan={5} className="empty">기간을 선택해주세요</td></tr>
                        : hDisplayRows.map(r => (
                          <tr key={r.date} style={r.isClosed ? {background:'#f3e5f5'} : (!r.clock_in ? {background:'#fafafa'} : {})}>
                            <td className="mono">{r.date}</td>
                            <td style={{
                              fontSize:12, fontWeight:600,
                              color: r.dow === '일' ? 'var(--danger)' : r.dow === '토' ? 'var(--accent2)' : 'var(--text2)',
                            }}>{r.dow}</td>
                            <td>
                              {r.clock_in
                                ? <span style={{fontFamily:'var(--mono)', color:'var(--success)', fontWeight:600}}>{fmt(r.clock_in)}</span>
                                : r.isClosed
                                  ? <span style={{padding:'2px 8px', background:'#f3e5f5', color:'#6a1b9a', border:'1px solid #ce93d8', borderRadius:3, fontSize:11, fontWeight:700}}>🏪 휴점</span>
                                  : <span style={{padding:'2px 8px', background:'#ffebee', color:'var(--danger)', border:'1px solid #f48fb1', borderRadius:3, fontSize:11, fontWeight:700}}>미체크</span>
                              }
                            </td>
                            <td style={{fontFamily:'var(--mono)', color:'var(--accent)', fontWeight:600}}>{fmt(r.clock_out)}</td>
                            <td className="mono" style={{color:'var(--text2)'}}>{duration(r.clock_in, r.clock_out)}</td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </div>
              )
          }
        </div>
      )}

      {tab === 'leave' && (
        <div className="card" style={{padding:'16px 20px'}}>
          {loading ? <div className="empty"><span className="spinner"/></div> : (
            <div className="twrap">
              <table>
                <thead>
                  <tr><th>신청월</th><th>매니저</th><th>점포</th><th>지점</th><th>선택날짜</th><th>일수</th><th>메모</th><th>제출일</th><th>상태</th></tr>
                </thead>
                <tbody>
                  {plans.length === 0
                    ? <tr><td colSpan={9} className="empty">연차계획 신청 내역이 없습니다</td></tr>
                    : plans.map(p => (
                      <tr key={p.id}>
                        <td className="mono" style={{fontWeight:700}}>{p.target_month}</td>
                        <td><strong>{p.manager_name}</strong></td>
                        <td><span className="badge badge-dept">{p.store_name}</span></td>
                        <td><span className="badge badge-store">{p.branch_name}</span></td>
                        <td style={{fontSize:11, color:'var(--text2)', maxWidth:220}}>
                          <button type="button" onClick={() => setLeaveCalTarget(p)}
                            style={{marginBottom:6, height:26, padding:'0 10px', border:'1px solid #1565C0', borderRadius:4, background:'#e3f2fd', color:'#1565C0', fontSize:11, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap'}}>
                            📅 달력보기
                          </button>
                          <div style={{display:'flex',flexWrap:'wrap',gap:3}}>{(p.dates||[]).map(d=><span key={d} style={{background:'#fff3e0',color:'var(--accent)',border:'1px solid #ffcc80',borderRadius:3,padding:'1px 6px',fontSize:10,fontWeight:600,whiteSpace:'nowrap'}}>{d}</span>)}</div>
                        </td>
                        <td style={{textAlign:'center', fontWeight:700, color:'var(--accent)'}}>{(p.dates||[]).length}일</td>
                        <td style={{fontSize:11, color:'var(--text3)'}}>{p.memo||'-'}</td>
                        <td className="mono" style={{fontSize:11}}>{new Date(p.created_at).toLocaleDateString('ko-KR')}</td>
                        <td>
                          {p.status === 'pending' ? (
                            <div style={{display:'flex', gap:4}}>
                              <button className="btn btn-p" style={{padding:'3px 8px', fontSize:11}}
                                onClick={() => updatePlanStatus(p.id, 'approved')}>확인</button>
                              <button className="btn-danger" style={{padding:'3px 8px', fontSize:11}}
                                onClick={() => { setRejectPlan(p); setRejectReason(''); }}>반려</button>
                            </div>
                          ) : (
                            <div>
                              <span style={{
                                display:'inline-block', padding:'2px 8px', borderRadius:4, fontSize:11, fontWeight:600,
                                background: p.status==='approved'?'#e8f5e9':'#ffebee',
                                color: p.status==='approved'?'var(--success)':'var(--danger)',
                              }}>
                                {p.status==='approved'?'✅ 확인':'❌ 반려'}
                              </span>
                              {p.status==='rejected' && p.reject_reason && (
                                <div style={{marginTop:4, fontSize:11, color:'var(--danger)', maxWidth:200, whiteSpace:'pre-wrap'}}>
                                  사유: {p.reject_reason}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'workers' && (() => {
        const wStores = [...new Set(workers.map(m => m.store?.department).filter(Boolean))].sort();
        const rank = t => t === '매니저' ? 0 : t === '부매니저' ? 1 : 2;
        const rows = workers
          .filter(m => showResigned || !m.resigned_at)
          .filter(m => !wStore || m.store?.department === wStore)
          .sort((a, b) => {
            const sa = a.store?.department || '', sb = b.store?.department || '';
            if (sa !== sb) return sa.localeCompare(sb);
            const ba = a.store?.branch || '', bb = b.store?.branch || '';
            if (ba !== bb) return ba.localeCompare(bb);
            return rank(a.job_title) - rank(b.job_title);
          });
        const activeCount = workers.filter(m => !m.resigned_at).length;
        return (
          <div className="card" style={{padding:'16px 20px'}}>
            <div className="fbar" style={{flexWrap:'wrap'}}>
              <select className="fsel" value={wStore} onChange={e => setWStore(e.target.value)}>
                <option value="">전체 점포</option>{wStores.map(s => <option key={s}>{s}</option>)}
              </select>
              <label style={{display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--text2)', cursor:'pointer'}}>
                <input type="checkbox" checked={showResigned} onChange={e => setShowResigned(e.target.checked)}/>
                퇴사자 포함
              </label>
              <button type="button" onClick={() => exportWorkers(rows)}
                title="근무자현황 엑셀 다운로드 (관리 열 제외)"
                style={{height:32, padding:'0 12px', border:'1px solid var(--accent)', borderRadius:'var(--radius)', background:'#fff3e0', color:'var(--accent)', fontSize:12, fontWeight:700, cursor:'pointer'}}>
                📥 엑셀
              </button>
              {canEdit && (
                <button type="button" onClick={() => setShowAddW(true)}
                  style={{height:32, padding:'0 14px', border:'1px solid #2e7d32', borderRadius:'var(--radius)', background:'#e8f5e9', color:'#2e7d32', fontSize:12, fontWeight:700, cursor:'pointer'}}>
                  ＋ 근무자 추가
                </button>
              )}
              <div className="fbar-right" style={{display:'flex', alignItems:'center', gap:14}}>
                <span className="fresult">재직 <b>{activeCount}</b>명 · 표시 <b>{rows.length}</b>명</span>
              </div>
            </div>
            {loadingW ? <div className="empty"><span className="spinner"/></div>
            : rows.length === 0 ? <div className="empty">근무자가 없습니다</div>
            : (
              <div className="twrap">
                <table>
                  <thead>
                    <tr><th>점포</th><th>지점</th><th>이름</th><th>직책</th><th>소속</th><th>연락처</th><th>입사일</th><th style={{textAlign:'center'}}>상태</th>{canEdit && <th style={{textAlign:'center'}}>관리</th>}</tr>
                  </thead>
                  <tbody>
                    {rows.map(m => (
                      <tr key={m.id} style={m.resigned_at ? {background:'#fafafa', opacity:0.7} : {}}>
                        <td><span className="badge badge-dept">{m.store?.department || '-'}</span></td>
                        <td><span className="badge badge-store">{m.store?.branch || '-'}</span></td>
                        <td style={{fontWeight:600}}>{m.display_name || m.name}</td>
                        <td>
                          <span style={{fontSize:12, fontWeight:600, color: m.job_title==='매니저'?'var(--accent)':'var(--text2)'}}>{m.job_title || '-'}</span>
                        </td>
                        <td style={{fontSize:12, color:'var(--text2)'}}>{m.affiliation || '-'}</td>
                        <td className="mono" style={{fontSize:12}}>{m.phone || '-'}</td>
                        <td className="mono" style={{fontSize:12, color:'var(--text3)'}}>{m.hire_date || '-'}</td>
                        <td style={{textAlign:'center'}}>
                          {m.resigned_at
                            ? <span style={{fontSize:11, fontWeight:700, color:'var(--danger)', background:'#ffebee', border:'1px solid #f48fb1', borderRadius:4, padding:'2px 8px'}}>퇴사 {String(m.resigned_at).slice(0,10)}</span>
                            : <span style={{fontSize:11, fontWeight:700, color:'#2e7d32', background:'#e8f5e9', border:'1px solid #a5d6a7', borderRadius:4, padding:'2px 8px'}}>재직</span>}
                        </td>
                        {canEdit && (
                          <td style={{textAlign:'center'}}>
                            <div style={{display:'flex', gap:4, justifyContent:'center'}}>
                              {!m.resigned_at && (
                                <button type="button" onClick={() => openEditWorker(m)} disabled={savingW === m.id}
                                  style={{height:28, padding:'0 10px', border:'1px solid var(--accent)', borderRadius:4, background:'#fff3e0', color:'var(--accent)', fontSize:11, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap'}}>
                                  정보수정
                                </button>
                              )}
                              <button type="button" onClick={() => toggleResign(m)} disabled={savingW === m.id}
                                style={{height:28, padding:'0 10px', border:`1px solid ${m.resigned_at ? '#2e7d32' : 'var(--danger)'}`, borderRadius:4, background:'#fff', color: m.resigned_at ? '#2e7d32' : 'var(--danger)', fontSize:11, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap'}}>
                                {savingW === m.id ? '처리중' : m.resigned_at ? '복귀' : '퇴사처리'}
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      {leaveCalTarget && (
        <LeaveCalendarModal plan={leaveCalTarget} onClose={() => setLeaveCalTarget(null)}/>
      )}

      {/* 근무자 정보 수정 모달 */}
      {eWorker && (() => {
        const eStores = [...new Set(storeAccounts.map(a => a.department))].sort();
        const eBranches = [...new Set(storeAccounts.filter(a => a.department === eStore).map(a => a.branch))].sort();
        const inp = {width:'100%', height:40, padding:'0 12px', border:'1px solid var(--border)', borderRadius:8, fontSize:14, outline:'none', boxSizing:'border-box'};
        const lbl = {display:'block', fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:5, marginTop:12};
        const jobOpts = JOB_TITLES.includes(eJob) ? JOB_TITLES : [eJob, ...JOB_TITLES];
        return (
          <div style={{position:'fixed', inset:0, zIndex:10000, display:'flex', alignItems:'center', justifyContent:'center'}}>
            <div style={{position:'absolute', inset:0, background:'rgba(0,0,0,0.5)'}} onClick={() => setEWorker(null)}/>
            <div style={{position:'relative', background:'#fff', borderRadius:12, padding:'22px 24px', width:440, maxWidth:'92vw', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 8px 40px rgba(0,0,0,0.22)'}}>
              <div style={{fontSize:16, fontWeight:700, marginBottom:14}}>근무자 정보 수정</div>
              <label style={lbl}>이름 <span style={{color:'var(--danger)'}}>*</span></label>
              <input value={eName} onChange={e => setEName(e.target.value)} placeholder="홍길동" style={inp}/>
              <label style={lbl}>점포 <span style={{color:'var(--danger)'}}>*</span></label>
              <select value={eStore} onChange={e => { setEStore(e.target.value); setEBranch(''); }} style={inp}>
                <option value="">점포 선택</option>{eStores.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <label style={lbl}>지점 <span style={{color:'var(--danger)'}}>*</span></label>
              <select value={eBranch} onChange={e => setEBranch(e.target.value)} disabled={!eStore} style={{...inp, background: eStore?'#fff':'#f0f0f0'}}>
                <option value="">{eStore ? '지점 선택' : '점포 먼저 선택'}</option>{eBranches.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
              <label style={lbl}>연락처</label>
              <input value={ePhone} onChange={e => setEPhone(formatPhone(e.target.value))} placeholder="010-0000-0000" inputMode="numeric" style={inp}/>
              <label style={lbl}>직책 <span style={{color:'var(--danger)'}}>*</span></label>
              <select value={eJob} onChange={e => setEJob(e.target.value)} style={inp}>
                {jobOpts.map(j => <option key={j} value={j}>{j}</option>)}
              </select>
              <label style={lbl}>소속</label>
              <select value={eAffil} onChange={e => setEAffil(e.target.value)} style={inp}>
                <option value="">(선택 안 함)</option>
                {AFFILIATIONS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              <div style={{display:'flex', gap:8, marginTop:20}}>
                <button type="button" onClick={() => setEWorker(null)}
                  style={{flex:1, height:42, border:'1px solid var(--border)', background:'#fff', color:'var(--text2)', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer'}}>취소</button>
                <button type="button" onClick={submitEditWorker} disabled={savingW === eWorker.id}
                  style={{flex:2, height:42, border:'none', background:'var(--accent)', color:'#fff', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer'}}>
                  {savingW === eWorker.id ? '저장중...' : '💾 저장'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* 근무자 추가 모달 */}
      {showAddW && (() => {
        const aStores = [...new Set(storeAccounts.map(a => a.department))].sort();
        const aBranches = [...new Set(storeAccounts.filter(a => a.department === aStore).map(a => a.branch))].sort();
        const inp = {width:'100%', height:40, padding:'0 12px', border:'1px solid var(--border)', borderRadius:8, fontSize:14, outline:'none', boxSizing:'border-box'};
        const lbl = {display:'block', fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:5, marginTop:12};
        return (
          <div style={{position:'fixed', inset:0, zIndex:10000, display:'flex', alignItems:'center', justifyContent:'center'}}>
            <div style={{position:'absolute', inset:0, background:'rgba(0,0,0,0.5)'}} onClick={() => setShowAddW(false)}/>
            <div style={{position:'relative', background:'#fff', borderRadius:12, padding:'22px 24px', width:440, maxWidth:'92vw', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 8px 40px rgba(0,0,0,0.22)'}}>
              <div style={{fontSize:16, fontWeight:700, marginBottom:4}}>＋ 근무자 추가</div>
              <div style={{fontSize:12, color:'var(--text3)', marginBottom:6}}>점포·지점을 선택하고 근무자 정보를 입력하세요.</div>
              <label style={lbl}>점포 <span style={{color:'var(--danger)'}}>*</span></label>
              <select value={aStore} onChange={e => { setAStore(e.target.value); setABranch(''); }} style={inp}>
                <option value="">점포 선택</option>{aStores.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <label style={lbl}>지점 <span style={{color:'var(--danger)'}}>*</span></label>
              <select value={aBranch} onChange={e => setABranch(e.target.value)} disabled={!aStore} style={{...inp, background: aStore?'#fff':'#f0f0f0'}}>
                <option value="">{aStore ? '지점 선택' : '점포 먼저 선택'}</option>{aBranches.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
              <label style={lbl}>이름 <span style={{color:'var(--danger)'}}>*</span></label>
              <input value={aName} onChange={e => setAName(e.target.value)} placeholder="홍길동" style={inp}/>
              <label style={lbl}>연락처</label>
              <input value={aPhone} onChange={e => setAPhone(formatPhone(e.target.value))} placeholder="010-0000-0000" inputMode="numeric" style={inp}/>
              <label style={lbl}>소속 <span style={{color:'var(--danger)'}}>*</span></label>
              <select value={aAffil} onChange={e => setAAffil(e.target.value)} style={inp}>
                {AFFILIATIONS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              <label style={lbl}>직책 <span style={{color:'var(--danger)'}}>*</span></label>
              <select value={aJob} onChange={e => setAJob(e.target.value)} style={inp}>
                {JOB_TITLES.map(j => <option key={j} value={j}>{j}</option>)}
              </select>
              <label style={lbl}>급여형태 <span style={{color:'var(--danger)'}}>*</span></label>
              <select value={aSalaryType} onChange={e => setASalaryType(e.target.value)} style={inp}>
                <option value="월급">월급</option>
                <option value="일급">일급</option>
              </select>
              <label style={lbl}>{aSalaryType === '월급' ? '월 급여' : '일급'} (원) <span style={{color:'var(--danger)'}}>*</span></label>
              <input value={aSalary ? Number(aSalary).toLocaleString() : ''} onChange={e => setASalary(e.target.value.replace(/[^0-9]/g, ''))}
                inputMode="numeric" placeholder={aSalaryType === '월급' ? '예: 2,350,000' : '예: 100,000'} style={inp}/>
              {aSalaryType === '일급' && (
                <>
                  <label style={lbl}>추가수당 <span style={{fontWeight:400, color:'var(--text3)'}}>(금·토·일 연장, 원)</span></label>
                  <input value={aExtra ? Number(aExtra).toLocaleString() : ''} onChange={e => setAExtra(e.target.value.replace(/[^0-9]/g, ''))}
                    inputMode="numeric" placeholder="예: 6,000" style={inp}/>
                </>
              )}
              <label style={lbl}>입사일 <span style={{fontWeight:400, color:'var(--text3)'}}>(생략 가능)</span></label>
              <input type="date" value={aHire} onChange={e => setAHire(e.target.value)} style={inp}/>
              <div style={{display:'flex', gap:8, marginTop:20}}>
                <button type="button" onClick={() => setShowAddW(false)}
                  style={{flex:1, height:42, border:'1px solid var(--border)', background:'#fff', color:'var(--text2)', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer'}}>취소</button>
                <button type="button" onClick={submitAddWorker} disabled={savingW === 'new'}
                  style={{flex:2, height:42, border:'none', background:'var(--accent)', color:'#fff', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer'}}>
                  {savingW === 'new' ? '추가중...' : '✅ 근무자 추가'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* 퇴사일 선택 모달 */}
      {resignTarget && (
        <div style={{position:'fixed', inset:0, zIndex:10000, display:'flex', alignItems:'center', justifyContent:'center'}}>
          <div style={{position:'absolute', inset:0, background:'rgba(0,0,0,0.5)'}} onClick={() => setResignTarget(null)}/>
          <div style={{position:'relative', background:'#fff', borderRadius:12, padding:'22px 24px', width:400, maxWidth:'92vw', boxShadow:'0 8px 40px rgba(0,0,0,0.22)'}}>
            <div style={{fontSize:16, fontWeight:700, marginBottom:6}}>퇴사 처리</div>
            <div style={{fontSize:12, color:'var(--text3)', marginBottom:16}}>
              <b>{resignTarget.display_name || resignTarget.name}</b> · {resignTarget.store?.department} {resignTarget.store?.branch}
            </div>
            <label style={{display:'block', fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:6}}>퇴사일 (마지막 근무일) <span style={{color:'var(--danger)'}}>*</span></label>
            <input type="date" value={resignDate} onChange={e => setResignDate(e.target.value)}
              style={{width:'100%', height:40, padding:'0 12px', border:'1px solid var(--border)', borderRadius:8, fontSize:14, outline:'none', boxSizing:'border-box'}}/>
            <div style={{fontSize:11, color:'var(--text3)', marginTop:8, lineHeight:1.6}}>
              · 퇴사일이 속한 <b>달까지는 급여계산에 포함</b>됩니다 (마지막 근무일까지).<br/>
              · 그 이후 출퇴근현황·연차·담당자 선택 등 활성 목록에서 제외됩니다.
            </div>
            <div style={{display:'flex', gap:8, marginTop:16}}>
              <button type="button" onClick={() => setResignTarget(null)}
                style={{flex:1, height:40, border:'1px solid var(--border)', background:'#fff', color:'var(--text2)', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer'}}>취소</button>
              <button type="button" onClick={submitResign} disabled={savingW === resignTarget.id}
                style={{flex:1, height:40, border:'none', background:'var(--danger)', color:'#fff', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer'}}>
                {savingW === resignTarget.id ? '처리중' : '퇴사 확정'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 연차 반려 사유 입력 모달 */}
      {rejectPlan && (
        <div style={{position:'fixed', inset:0, zIndex:10000, display:'flex', alignItems:'center', justifyContent:'center'}}>
          <div style={{position:'absolute', inset:0, background:'rgba(0,0,0,0.5)'}} onClick={() => setRejectPlan(null)}/>
          <div style={{position:'relative', background:'#fff', borderRadius:12, padding:'22px 24px', width:420, maxWidth:'92vw', boxShadow:'0 8px 40px rgba(0,0,0,0.22)'}}>
            <div style={{fontSize:16, fontWeight:700, marginBottom:6}}>연차 반려</div>
            <div style={{fontSize:12, color:'var(--text3)', marginBottom:14}}>
              <b>{rejectPlan.manager_name}</b> · {rejectPlan.target_month} · {(rejectPlan.dates||[]).length}일
            </div>
            <label style={{display:'block', fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:6}}>반려 사유 <span style={{color:'var(--danger)'}}>*</span></label>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
              placeholder="예: 해당 기간 인원 부족으로 조정 필요 / 성수기 연차 제한 등"
              style={{width:'100%', minHeight:90, padding:'10px 12px', border:'1px solid var(--border)', borderRadius:8, fontSize:13, fontFamily:'inherit', outline:'none', resize:'vertical', boxSizing:'border-box'}}
              autoFocus/>
            <div style={{fontSize:11, color:'var(--text3)', marginTop:6}}>* 반려 사유는 신청한 매장 근무자에게 표시됩니다.</div>
            <div style={{display:'flex', gap:8, marginTop:16}}>
              <button type="button" onClick={() => setRejectPlan(null)}
                style={{flex:1, height:40, border:'1px solid var(--border)', background:'#fff', color:'var(--text2)', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer'}}>취소</button>
              <button type="button" onClick={submitReject}
                style={{flex:1, height:40, border:'none', background:'var(--danger)', color:'#fff', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer'}}>반려 확정</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
